'use client';
import { useEffect, useState } from 'react';
import type { ConversationTreeNode } from './conversationTree';

/** Page the already filtered, section-ordered roots, independently of branch enrichment.
 *  The caller supplies a stable serialized facet key; branch responses never enter reset inputs. */
export function useConversationPaging(
  visible: readonly ConversationTreeNode[],
  page: number,
  pageSize: number,
  setPage: (next: number | ((current: number) => number)) => void,
  setPageIds: (ids: readonly string[]) => void,
  search: string,
  sort: string,
  direction: 'asc' | 'desc',
  facets = '',
): {
  clampedPage: number;
  pageRows: ConversationTreeNode[];
} {
  const inputKey = JSON.stringify([search, sort, direction, facets]);
  const [previousInputs, setPreviousInputs] = useState(inputKey);
  const resetting = previousInputs !== inputKey;
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  // Use the reset page in this render too: no stale-page branch request may escape before the effect.
  const clampedPage = resetting ? 0 : Math.min(page, pageCount - 1);
  const pageRows = visible.slice(clampedPage * pageSize, (clampedPage + 1) * pageSize);
  const pageKey = JSON.stringify(pageRows.map((node) => node.row.id));

  useEffect(() => {
    setPreviousInputs(inputKey);
    setPage(0);
  }, [inputKey, setPage]);
  useEffect(() => { setPageIds(JSON.parse(pageKey) as string[]); }, [pageKey, setPageIds]);

  return { clampedPage, pageRows };
}
